import { useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { Header } from './Components/Header'
import { PickupContent } from './Components/Pickup'
import { DeliveryPrompt } from './Components/Delivery'
import { 
  ShoppingBagIcon, 
  TruckIcon,
} from '@/components/ui/icons'
import type { RootState } from '@/App/store'
import type { Store } from '@/types'
import { getStorePickupStatus } from '@/utils/storePickupStatus'
import { selectIsDarkMode, toggleDarkMode } from '@/App/nightModeSlice'
import { loadRestaurantFromWindow } from '@/App/restaurantSlice'
import { selectFulfillmentMode, setFulfillmentMode } from '@/App/fulfillmentModeSlice'

export function StoreSelectionScreen() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const allStores: Store[] = useSelector((s: RootState) => s.store.store?.stores ?? [])
  const storesLoading = useSelector((s: RootState) => s.store.loading)
  const darkMode = useSelector(selectIsDarkMode)
  const restaurant = useSelector((s: RootState) => s.restaurant.restaurant)
  const fulfillmentMode = useSelector(selectFulfillmentMode)
  const filteredStores = useMemo(() => {
    // Show all stores that are not explicitly hidden, and sort so open ones appear first
    const sorted = [...allStores].sort((a, b) => {
      const statusA = getStorePickupStatus(a).isAvailableForPickup
      const statusB = getStorePickupStatus(b).isAvailableForPickup
      if (statusA === statusB) return 0
      return statusA ? -1 : 1
    })
    return sorted
  }, [allStores])

  useEffect(() => {
    dispatch(loadRestaurantFromWindow())
  }, [dispatch])

  const fulfillmentOptions = [
    { value: 'pickup', label: 'Pickup', icon: <ShoppingBagIcon className="size-5" /> },
    { value: 'delivery', label: 'Delivery', icon: <TruckIcon className="size-5" /> },
  ]

  return (
    <div 
      className="min-h-dvh flex flex-col"
    >
      <Header
        logoUrl={undefined}
        restaurantName={restaurant?.name || ''}
        tagline={undefined}
        darkMode={darkMode}
        onToggleDarkMode={() => dispatch(toggleDarkMode())}
      />

      {/* Main Content */}
      <main className="flex-1 -mt-4 relative">
        <div className="bg-surface-secondary rounded-t-3xl min-h-full max-w-6xl mx-auto">
          {/* Toggle Section */}
          <div className="px-4 pt-6 pb-4">
            <SegmentedControl
              options={fulfillmentOptions}
              value={fulfillmentMode}
              onChange={(value) => dispatch(setFulfillmentMode(value as 'pickup' | 'delivery'))}
            />
          </div>

          {/* Content based on mode */}
          <div className="px-4 pb-8">
            {fulfillmentMode === 'pickup' ? (
              <PickupContent
                filteredStores={filteredStores}
                onViewMap={() => navigate('/map')}
                loading={storesLoading}
              />
            ) : (
              <DeliveryPrompt
                stores={allStores}
                onSearchAddress={() => navigate('/address-search')}
              />
            )}
          </div>
        </div>
      </main>

      {/* Screen reader live region */}
      <div className="sr-only" role="status" aria-live="polite">
        {fulfillmentMode === 'pickup' 
          ? `${filteredStores.length} stores available for pickup`
          : 'Delivery mode selected. Enter your address to see available stores.'
        }
      </div>
    </div>
  )
}

